/**
 * Root normalisation, validation and the bone-reference rewriter (docs/SKEL-EDITOR.md §3.1, §5.1).
 *
 * A SkelForm file is only runnable in one forward pass when `bones[i].id == i` and every parent
 * comes before its children (report 01 §4.2), and every other structure points at bones by index:
 * `parent_id`, keyframe `bone_id`, IK `bone_ids`/`target_id`, mesh bind `bone_id`. The same
 * rewriter (`applyBoneOrder`) therefore serves both loading (0.7 files, hand-written ids) and every
 * structural edit in `edit.ts`, so there is exactly one place that knows the full reference list.
 *
 * `normalizeRoot` mirrors the backend's `skf.normalize_root`: 0.7 → 0.8 defaults (white tint,
 * `init_*` = the base field, `pivot_scale` (1,1)), keyframes stably sorted by frame with `next_kf`
 * recomputed, ids = indices. Unlike the backend it repairs a non-parent-first order (a stable DFS
 * with every reference remapped) instead of rejecting it; only a parent cycle is fatal.
 */
import { presetOf } from './math'
import { SKF_VERSION, type IkConstraint, type SkfAnimation, type SkfKeyframe, type SkfRoot, type Tint, type V2 } from './types'

/** Deep copy of a JSON-shaped root. JSON (not structuredClone) so immer drafts clone too. */
export function cloneRoot<T = SkfRoot>(root: T): T {
  return JSON.parse(JSON.stringify(root)) as T
}

// ------------------------------------------------------------------------------------ keyframes

/** Stable sort by frame (SkelForm `sort_keyframes`); ties keep their array order. */
export function sortKeyframes(anim: SkfAnimation): void {
  const indexed = anim.keyframes.map((kf, i) => ({ kf, i }))
  indexed.sort((a, b) => a.kf.frame - b.kf.frame || a.i - b.i)
  anim.keyframes = indexed.map((e) => e.kf)
}

/**
 * `next_kf` = index of the key with the same bone and element and the smallest frame strictly
 * greater than this key's (ties → the lowest index), −1 if none. For a frame-sorted array this is
 * exactly the editor's "first index j scanning from 0 with frame_j > frame_k" (report 01 §4.8.3).
 */
export function recomputeNextKf(anim: SkfAnimation): void {
  const channels = new Map<string, number[]>()
  anim.keyframes.forEach((kf, i) => {
    const key = `${kf.bone_id}|${kf.element}`
    const list = channels.get(key)
    if (list) list.push(i)
    else channels.set(key, [i])
  })
  const kfs = anim.keyframes
  for (const list of channels.values()) {
    const sorted = [...list].sort((a, b) => kfs[a]!.frame - kfs[b]!.frame || a - b)
    let j = 0
    for (let p = 0; p < sorted.length; p++) {
      const idx = sorted[p]!
      const f = kfs[idx]!.frame
      if (j <= p) j = p + 1
      while (j < sorted.length && kfs[sorted[j]!]!.frame <= f) j++
      kfs[idx]!.next_kf = j < sorted.length ? sorted[j]! : -1
    }
  }
}

// ------------------------------------------------------------------------- bone reference rewrite

/**
 * Rewrite every bone reference through `map` (old index → new index, −1 = the bone is gone):
 * parent ids, keyframe bone ids (keys of gone bones are dropped, `next_kf` recomputed), IK chain
 * members (gone members removed) and targets (gone → −1), mesh bind bones (gone → −1).
 * References that were already out of range are treated as gone.
 */
export function rewriteBoneRefs(root: SkfRoot, map: (old: number) => number): void {
  const m = (old: number): number => (Number.isInteger(old) && old >= 0 ? map(old) : -1)
  for (const b of root.bones) b.parent_id = b.parent_id === -1 ? -1 : m(b.parent_id)
  for (const anim of root.animations ?? []) {
    const before = anim.keyframes.length
    let changed = false
    const kept: SkfKeyframe[] = []
    for (const kf of anim.keyframes) {
      const id = m(kf.bone_id)
      if (id < 0) continue
      if (id !== kf.bone_id) changed = true
      kf.bone_id = id
      kept.push(kf)
    }
    if (changed || kept.length !== before) {
      anim.keyframes = kept
      recomputeNextKf(anim)
    }
  }
  for (const ik of root.inverse_kinematics) {
    ik.bone_ids = (ik.bone_ids ?? []).map(m).filter((id) => id >= 0)
    ik.target_id = ik.target_id === -1 ? -1 : m(ik.target_id)
  }
  for (const vis of root.visuals) {
    for (const bind of vis.binds ?? []) bind.bone_id = bind.bone_id === -1 ? -1 : m(bind.bone_id)
  }
}

/**
 * Rebuild `root.bones` in `order` (a list of OLD indices; bones not listed are deleted), set
 * `id` = new index and remap every reference. Returns the old → new map (−1 = deleted).
 * The caller guarantees `order` is parent-first.
 */
export function applyBoneOrder(root: SkfRoot, order: number[]): number[] {
  const oldToNew = new Array<number>(root.bones.length).fill(-1)
  order.forEach((old, i) => {
    oldToNew[old] = i
  })
  const bones = order.map((old) => root.bones[old]!)
  root.bones = bones
  rewriteBoneRefs(root, (old) => oldToNew[old] ?? -1)
  bones.forEach((b, i) => {
    b.id = i
  })
  return oldToNew
}

/**
 * Drop visuals and physics entries no bone references, and IK families with no chain bones left;
 * renumber `visuals_id` / `physics_id` / `ik_family_id` and the families' `id`s to match.
 */
export function compactRefs(root: SkfRoot): void {
  // visuals
  const visUsed = root.visuals.map(() => false)
  for (const b of root.bones) if (b.visuals_id >= 0 && b.visuals_id < visUsed.length) visUsed[b.visuals_id] = true
  const visMap = remapUsed(visUsed)
  root.visuals = root.visuals.filter((_, i) => visUsed[i])
  for (const b of root.bones) b.visuals_id = b.visuals_id >= 0 ? (visMap[b.visuals_id] ?? -1) : -1

  // physics
  const phUsed = root.physics.map(() => false)
  for (const b of root.bones) if (b.physics_id >= 0 && b.physics_id < phUsed.length) phUsed[b.physics_id] = true
  const phMap = remapUsed(phUsed)
  root.physics = root.physics.filter((_, i) => phUsed[i])
  for (const b of root.bones) b.physics_id = b.physics_id >= 0 ? (phMap[b.physics_id] ?? -1) : -1

  // IK families: a family survives while it still lists at least one bone
  const ikUsed = root.inverse_kinematics.map((ik) => (ik.bone_ids ?? []).length > 0)
  const ikMap = remapUsed(ikUsed)
  root.inverse_kinematics = root.inverse_kinematics.filter((_, i) => ikUsed[i])
  root.inverse_kinematics.forEach((ik, i) => {
    ik.id = i
  })
  for (const b of root.bones) b.ik_family_id = b.ik_family_id >= 0 ? (ikMap[b.ik_family_id] ?? -1) : -1
}

function remapUsed(used: boolean[]): number[] {
  let next = 0
  return used.map((u) => (u ? next++ : -1))
}

/** Bone indices in a parent-first DFS (roots and siblings in array order), or null on a cycle. */
export function dfsOrder(root: SkfRoot): number[] | null {
  const n = root.bones.length
  const children: number[][] = Array.from({ length: n }, () => [])
  const roots: number[] = []
  root.bones.forEach((b, i) => {
    const p = b.parent_id
    if (p >= 0 && p < n && p !== i) children[p]!.push(i)
    else roots.push(i)
  })
  const order: number[] = []
  const stack = [...roots].reverse()
  while (stack.length) {
    const i = stack.pop()!
    order.push(i)
    const kids = children[i]!
    for (let k = kids.length - 1; k >= 0; k--) stack.push(kids[k]!)
  }
  return order.length === n ? order : null
}

export function isParentFirst(root: SkfRoot): boolean {
  return root.bones.every((b, i) => b.parent_id >= -1 && b.parent_id < i)
}

// ------------------------------------------------------------------------------- normalisation

const WHITE: Tint = { r: 1, g: 1, b: 1, a: 1 }

function num(v: unknown, d: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : d
}

function vec(v: unknown, d: V2): V2 {
  const o = v as { x?: unknown; y?: unknown } | null | undefined
  return { x: num(o?.x, d.x), y: num(o?.y, d.y) }
}

function tint(v: unknown, d: Tint): Tint {
  const o = v as Partial<Record<'r' | 'g' | 'b' | 'a', unknown>> | null | undefined
  if (!o || typeof o !== 'object') return { ...d }
  return { r: num(o.r, d.r), g: num(o.g, d.g), b: num(o.b, d.b), a: num(o.a, d.a) }
}

const IK_CONSTRAINTS: readonly IkConstraint[] = ['None', 'Clockwise', 'CounterClockwise']

/** `IkConstraint` key value → name (prepare_files: 1 → Clockwise, 2 → CounterClockwise, else None). */
export function constraintFromValue(value: number): IkConstraint {
  return value === 1 ? 'Clockwise' : value === 2 ? 'CounterClockwise' : 'None'
}

export function constraintValue(c: string | undefined): number {
  return c === 'Clockwise' ? 1 : c === 'CounterClockwise' ? 2 : 0
}

/**
 * A normalised deep copy of `input` (never mutates it). Throws only when the bone hierarchy has a
 * cycle or `bones` is not an array. See the module comment for the defaults applied.
 */
export function normalizeRoot(input: SkfRoot): SkfRoot {
  const root = cloneRoot(input) as SkfRoot & Record<string, unknown>
  if (!Array.isArray(root.bones)) throw new Error('armature has no bones array')
  root.version = SKF_VERSION
  root.baked_ik = root.baked_ik === true
  root.img_format = root.img_format === 'JPG' ? 'JPG' : 'PNG'
  root.atlases = Array.isArray(root.atlases) ? root.atlases : []
  root.styles = Array.isArray(root.styles) ? root.styles : []
  root.visuals = Array.isArray(root.visuals) ? root.visuals : []
  root.inverse_kinematics = Array.isArray(root.inverse_kinematics) ? root.inverse_kinematics : []
  root.physics = Array.isArray(root.physics) ? root.physics : []
  root.animations = Array.isArray(root.animations) ? root.animations : []

  // ids → indices: when the file's ids are a unique set, references are in id space; translate.
  const ids = root.bones.map((b) => b.id)
  const idsAreIndices = ids.every((id, i) => id === i)
  if (!idsAreIndices && new Set(ids).size === ids.length && ids.every((id) => Number.isInteger(id))) {
    const idToIndex = new Map<number, number>(ids.map((id, i) => [id, i]))
    rewriteBoneRefs(root, (old) => idToIndex.get(old) ?? -1)
  }
  root.bones.forEach((b, i) => {
    b.id = i
    if (typeof b.parent_id !== 'number' || b.parent_id < -1 || b.parent_id >= root.bones.length || b.parent_id === i) {
      b.parent_id = -1
    }
  })

  // bones
  for (const b of root.bones) {
    b.name = typeof b.name === 'string' ? b.name : ''
    b.pos = vec(b.pos, { x: 0, y: 0 })
    b.scale = vec(b.scale, { x: 1, y: 1 })
    b.rot = num(b.rot, 0)
    b.init_pos = vec(b.init_pos, b.pos)
    b.init_rot = num(b.init_rot, b.rot)
    b.init_scale = vec(b.init_scale, b.scale)
    const hidden = b.hidden === true
    const initHidden = typeof b.init_hidden === 'boolean' ? b.init_hidden : hidden
    delete b.hidden
    delete b.init_hidden
    if (hidden) b.hidden = true
    if (initHidden) b.init_hidden = true
    b.ik_family_id = Number.isInteger(b.ik_family_id) ? b.ik_family_id : -1
    b.ik_family_name = typeof b.ik_family_name === 'string' ? b.ik_family_name : ''
    b.physics_id = Number.isInteger(b.physics_id) ? b.physics_id : -1
    b.visuals_id = Number.isInteger(b.visuals_id) ? b.visuals_id : -1
    if (b.ik_family_id < -1 || b.ik_family_id >= root.inverse_kinematics.length) b.ik_family_id = -1
    if (b.physics_id < -1 || b.physics_id >= root.physics.length) b.physics_id = -1
    if (b.visuals_id < -1 || b.visuals_id >= root.visuals.length) b.visuals_id = -1
  }

  // visuals (0.7 has no tint: white; init_tint defaults to tint)
  for (const v of root.visuals) {
    v.tex = typeof v.tex === 'string' ? v.tex : ''
    v.tint = tint(v.tint, WHITE)
    v.init_tint = tint(v.init_tint, v.tint)
    v.zindex = Math.round(num(v.zindex, 0))
    v.init_zindex = Math.round(num(v.init_zindex, v.zindex))
    v.init_tex = typeof v.init_tex === 'string' ? v.init_tex : v.tex
    v.pivot_pos = vec(v.pivot_pos, { x: 0, y: 0 })
    v.pivot_rot = num(v.pivot_rot, 0)
    v.pivot_scale = vec(v.pivot_scale, { x: 1, y: 1 })
    if (!Array.isArray(v.vertices) || v.vertices.length === 0) delete v.vertices
    if (!Array.isArray(v.indices) || v.indices.length === 0) delete v.indices
    if (!Array.isArray(v.binds) || v.binds.length === 0) delete v.binds
    if (v.vertices) {
      v.vertices.forEach((vert, i) => {
        vert.id = i
        vert.pos = vec(vert.pos, { x: 0, y: 0 })
        vert.init_pos = vec(vert.init_pos, vert.pos)
        vert.uv = vec(vert.uv, { x: 0, y: 0 })
      })
    }
  }

  // IK families
  root.inverse_kinematics.forEach((ik, i) => {
    ik.id = i
    ik.constraint = IK_CONSTRAINTS.includes(ik.constraint as IkConstraint) ? ik.constraint : 'None'
    ik.mode = ik.mode === 'Arc' ? 'Arc' : 'FABRIK'
    ik.target_id = Number.isInteger(ik.target_id) && ik.target_id >= 0 && ik.target_id < root.bones.length ? ik.target_id : -1
    ik.bone_ids = (Array.isArray(ik.bone_ids) ? ik.bone_ids : []).filter(
      (id) => Number.isInteger(id) && id >= 0 && id < root.bones.length,
    )
    ik.mimic_target = ik.mimic_target === true
    ik.init_constraint = IK_CONSTRAINTS.includes(ik.init_constraint as IkConstraint) ? ik.init_constraint : ik.constraint
    ik.init_mode = ik.init_mode === 'Arc' || ik.init_mode === 'FABRIK' ? ik.init_mode : ik.mode
    ik.init_mimic_target = typeof ik.init_mimic_target === 'boolean' ? ik.init_mimic_target : ik.mimic_target
  })

  // styles + textures
  root.styles.forEach((s, i) => {
    s.id = i
    s.name = typeof s.name === 'string' ? s.name : ''
    s.textures = Array.isArray(s.textures) ? s.textures : []
    for (const t of s.textures) {
      t.name = typeof t.name === 'string' ? t.name : ''
      t.offset = vec(t.offset, { x: 0, y: 0 })
      t.size = vec(t.size, { x: 0, y: 0 })
      t.atlas_idx = Number.isInteger(t.atlas_idx) ? t.atlas_idx : 0
    }
  })

  // animations
  root.animations.forEach((anim, i) => {
    anim.id = i
    anim.name = typeof anim.name === 'string' ? anim.name : ''
    // SkelForm's fps is an integer (a fractional one does not deserialise in the editor)
    anim.fps = num(anim.fps, 60) > 0 ? Math.max(1, Math.round(num(anim.fps, 60))) : 60
    const kfs = Array.isArray(anim.keyframes) ? anim.keyframes : []
    anim.keyframes = kfs.filter((kf) => num(kf.frame, -1) >= 0 && Number.isInteger(kf.bone_id))
    for (const kf of anim.keyframes) {
      kf.frame = Math.round(kf.frame)
      kf.value = num(kf.value, 0)
      kf.start_handle = vec(kf.start_handle, { x: 0, y: 0 })
      kf.end_handle = vec(kf.end_handle, { x: 0, y: 0 })
      if (kf.element === 'IkConstraint' && !kf.value_str) kf.value_str = constraintFromValue(kf.value)
      if (typeof kf.handle_preset !== 'string') kf.handle_preset = presetOf(kf.start_handle, kf.end_handle)
    }
    sortKeyframes(anim)
    recomputeNextKf(anim)
  })

  // parent-first order (repair instead of reject)
  if (!isParentFirst(root)) {
    const order = dfsOrder(root)
    if (!order) throw new Error('bone hierarchy has a cycle')
    applyBoneOrder(root, order)
  }
  // drop keys whose bone does not exist
  for (const anim of root.animations) {
    const n = root.bones.length
    if (anim.keyframes.some((kf) => kf.bone_id < 0 || kf.bone_id >= n)) {
      anim.keyframes = anim.keyframes.filter((kf) => kf.bone_id >= 0 && kf.bone_id < n)
      recomputeNextKf(anim)
    }
  }
  return root
}

// ---------------------------------------------------------------------------------- validation

function inRangeOrNone(v: unknown, n: number): boolean {
  return v === -1 || (Number.isInteger(v) && (v as number) >= 0 && (v as number) < n)
}

/**
 * The §3.1 structural invariants the server enforces on PUT; an empty list means valid. Never
 * throws: malformed entries (null bones, a missing `keyframes` array, …) are reported as errors.
 */
export function validateRoot(root: SkfRoot): string[] {
  const errors: string[] = []
  if (!root || typeof root !== 'object') return ['root must be an object']
  if (typeof root.version !== 'string') errors.push('root.version must be a string')
  if (!Array.isArray(root.bones)) return [...errors, 'root.bones must be an array']
  const n = root.bones.length
  const nVis = Array.isArray(root.visuals) ? root.visuals.length : 0
  const nIk = Array.isArray(root.inverse_kinematics) ? root.inverse_kinematics.length : 0
  const nPh = Array.isArray(root.physics) ? root.physics.length : 0
  root.bones.forEach((b, i) => {
    if (!isObject(b)) {
      errors.push(`bones[${i}] is not an object`)
      return
    }
    if (b.id !== i) errors.push(`bones[${i}].id is ${String(b.id)}, expected ${i}`)
    if (!Number.isInteger(b.parent_id) || b.parent_id < -1 || b.parent_id >= i) {
      errors.push(`bones[${i}].parent_id ${String(b.parent_id)} must be -1 or a bone before it`)
    }
    if (!inRangeOrNone(b.visuals_id, nVis)) errors.push(`bones[${i}].visuals_id ${String(b.visuals_id)} is out of range`)
    if (!inRangeOrNone(b.ik_family_id, nIk)) errors.push(`bones[${i}].ik_family_id ${String(b.ik_family_id)} is out of range`)
    if (!inRangeOrNone(b.physics_id, nPh)) errors.push(`bones[${i}].physics_id ${String(b.physics_id)} is out of range`)
  })
  if (root.animations !== undefined && !Array.isArray(root.animations)) errors.push('root.animations must be an array')
  ;(Array.isArray(root.animations) ? root.animations : []).forEach((anim, a) => {
    if (!isObject(anim) || !Array.isArray(anim.keyframes)) {
      errors.push(`animations[${a}] has no keyframes array`)
      return
    }
    anim.keyframes.forEach((kf, k) => {
      if (!isObject(kf) || !Number.isInteger(kf.bone_id) || kf.bone_id < 0 || kf.bone_id >= n) {
        errors.push(`animations[${a}].keyframes[${k}].bone_id ${String(isObject(kf) ? kf.bone_id : kf)} is out of range`)
      }
    })
  })
  ;(Array.isArray(root.inverse_kinematics) ? root.inverse_kinematics : []).forEach((ik, f) => {
    if (!isObject(ik)) {
      errors.push(`inverse_kinematics[${f}] is not an object`)
      return
    }
    if (!inRangeOrNone(ik.target_id, n)) errors.push(`inverse_kinematics[${f}].target_id ${String(ik.target_id)} is out of range`)
    for (const id of Array.isArray(ik.bone_ids) ? ik.bone_ids : []) {
      if (!Number.isInteger(id) || id < 0 || id >= n) errors.push(`inverse_kinematics[${f}].bone_ids has ${String(id)}, out of range`)
    }
  })
  ;(Array.isArray(root.visuals) ? root.visuals : []).forEach((v, vi) => {
    if (!isObject(v)) {
      errors.push(`visuals[${vi}] is not an object`)
      return
    }
    for (const bind of Array.isArray(v.binds) ? v.binds : []) {
      if (!isObject(bind) || !inRangeOrNone(bind.bone_id, n)) {
        errors.push(`visuals[${vi}] bind bone ${String(isObject(bind) ? bind.bone_id : bind)} is out of range`)
      }
    }
  })
  return errors
}

function isObject<T>(v: T | null | undefined): v is T {
  return typeof v === 'object' && v !== null
}
